import { useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { useHeaderContext } from "../context/headerContext";
import DotPagination from "../components/DotPagination";
import { Link } from "react-router-dom";
import PersonaliseImage from "/images/onboarding-images/Personalise.png";
import ProtectionImage from "/images/onboarding-images/Protection.png";
import ExploreImage from "/images/onboarding-images/Explore.png";
import TrackImage from "/images/onboarding-images/Swimming.png";
import BookImage from "/images/onboarding-images/Book.png";
import { useUserContext } from "../context/userContext";
import fetchWithRefresh from "../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../utils/LogErrors";

const cases = [
  {
    image: PersonaliseImage,
    title: "PERSONALISE",
    description1: "Activate your own Sports Guardian band",
    description2:
      "Share vital information with first responders when you are unable to communicate.",
  },
  {
    image: ProtectionImage,
    title: "PROTECTION",
    description1: "Speaks for you when you can't",
    description2:
      "Manually unlock the band's QR code to make your information visible in case of an incident.",
  },
  {
    image: ExploreImage,
    title: "EXPLORE",
    description1: "Discover your nearest Sports Guardian venues",
    description2:
      "Check in at any participating Sports Guardian club, venue or event.",
  },
  {
    image: TrackImage,
    title: "TRACK",
    description1: "Log your Sports Guardian band activity",
    description2: "See historical data of your Sports Guardian band usage.",
    link: "<a href='https://sportsguardian.b-cdn.net/SG_Member_Guide_Feb25.pdf' target='_blank'>Read Full User Guide</a>",
  },
  // {
  //   image: BookImage,
  //   title: "BOOK",
  //   description1: "Make a booking at any Sports Guardian venue",
  //   description2:
  //     "Book a session at your desired venue, directly within the app.",
  // },
];

export default function ParticipantOnboarding() {
  const { setTitle } = useHeaderContext();
  const { user, logout, updateTokens } = useUserContext();

  const [currentDot, setCurrentDot] = useState(1);
  const totalDots = cases.length;

  useEffect(() => {
    setTitle("W E L C O M E");

    const toggleOnboarding = async () => {
      return await Sentry.startSpan({ name: "Toggle Onboarding", op: "http.request" }, async (span) => {
        try {
          const url = import.meta.env.VITE_API_URL + "Users/SetUserOnboarding";
          span.setAttribute("http.url", url);
          span.setAttribute("user_id", user?.id);
    
          const response = await fetchWithRefresh(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
            body: JSON.stringify({
              userLoginId: user.id,
              requireOnboarding: 0,
            }),
          }, logout, updateTokens, (message) => { toast.info(message); });
    
          if (response.ok) {
            // LogErrors(user, logout, updateTokens, "Onboarding toggled successfully");
          } else {
            const errorMessage = `Failed to toggle onboarding: ${response.status} ${response.statusText}`;
            Sentry.captureMessage(errorMessage, "warning");
            LogErrors(user, logout, updateTokens, errorMessage);
          }
    
          span.setStatus(response.ok ? "ok" : "error");
        } catch (error) {
          Sentry.captureException(error, { extra: { user_id: user?.id } });
          LogErrors(user, logout, updateTokens, "Network error: " + error);
        }
      });
    };

    toggleOnboarding();
  }, []);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setCurrentDot((prev) => (prev === totalDots ? 1 : prev + 1));
    },
    onSwipedRight: () => {
      setCurrentDot((prev) => (prev === 1 ? totalDots : prev - 1));
    },
  });

  const getContentByDot = (dot) => {
    return cases[dot - 1];
  };

  const getOffset = () => {
    // Ensure the offset does not go below 0
    const offset = currentDot > 0 ? (currentDot - 1) * 100 : 0;
    return -offset; // Assuming 100% width per section
  };

  const content = getContentByDot(currentDot);

  if (!content) return null;

  return (
    <div {...handlers} className="relative overflow-hidden pb-24">
      <div
        className="flex transition-transform duration-500"
        style={{
          transform: `translateX(${getOffset()}%)`,
        }}
      >
        {cases.map((content, index) => (
          <div
            key={index}
            className="flex flex-col items-center text-center flex-[0_0_100%]"
          >
            <img
              src={content.image}
              className="mb-[60px] h-[333px] w-full object-cover"
              alt={content.title}
            />
            <div className="flex flex-col items-center justify-center text-center">
              <p className="tracking-[0.2em] mb-[10px] font-semibold">
                {content.title}
              </p>
              <p className="mb-[30px] text-[24px] font-semibold px-[32px]">
                {content.description1}
              </p>
              <p className="text-center text-[#727272] px-[20px]">
                {content.description2}
              </p>
              {content.link && (
                <div
                  className="underline mb-[44px]"
                  dangerouslySetInnerHTML={{ __html: content.link }}
                />
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-y-[40px] items-center">
        <DotPagination
          totalDots={cases.length}
          currentDot={currentDot}
          setCurrentDot={setCurrentDot}
        />
        <Link to="/" className="get-started-link font-semibold underline">
          Get Started
        </Link>
      </div>
    </div>
  );
}
